import { useLanguage } from "@/contexts/LanguageContext";
import { useLocation } from "react-router-dom";

export const useLocalizedPath = () => {
  const { language } = useLanguage();
  const location = useLocation();

  /**
   * Returns a localized path with language prefix
   * @param path - The path without language prefix (e.g., "/features")
   * @returns The path with language prefix if needed (e.g., "/sv/features")
   */
  const getLocalizedPath = (path: string): string => {
    // Check if current URL has a language prefix
    const pathParts = location.pathname.split('/').filter(Boolean);
    const hasLangPrefix = pathParts[0] === 'en' || pathParts[0] === 'sv';
    
    // If current page has a language prefix, maintain it
    if (hasLangPrefix) {
      const prefix = language === 'en' ? '/en' : '/sv';
      return `${prefix}${path}`;
    }
    
    // Default: Swedish gets prefix, English is default (no prefix)
    if (language === 'sv') {
      return `/sv${path}`;
    }
    
    return path;
  };

  /**
   * Switches the current path to a different language
   * @param newLang - The target language ('en' | 'sv')
   * @returns The current path with the new language prefix
   */
  const switchLanguagePath = (newLang: 'en' | 'sv'): string => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const hasLangPrefix = pathParts[0] === 'en' || pathParts[0] === 'sv';
    
    let basePath = hasLangPrefix 
      ? '/' + pathParts.slice(1).join('/')
      : location.pathname;
    
    if (basePath === '') basePath = '/';
    
    // English is default (no prefix needed), Swedish gets prefix
    if (newLang === 'sv') {
      return `/sv${basePath === '/' ? '' : basePath}`;
    }
    
    // For English, use the path with /en prefix for consistency when switching
    return `/en${basePath === '/' ? '' : basePath}`;
  };

  return { getLocalizedPath, switchLanguagePath };
};

export default useLocalizedPath;
